import {
  BillTypeData,
  CostShareStatus,
} from '../components/bills/bill-grid-components/bill-management-types';
import { formatCurrency } from './currency-helpers';

/**
 * @param string i.e carrier_change
 * @returns i.e Carrier change
 */
export const capitalizeFirstLetterAndSpaceSnakeString = (
  string = '',
): string => {
  const withSpaces = string.replace(/_/g, ' ');
  const capitalized = withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  return capitalized;
};

/**
 * @param string i.e billType
 * @returns i.e Bill type
 */
export const capitalizeFirstLetterAndSpaceCamelCaseString = (
  string = '',
): string => {
  const formatted = string.replace(/([A-Z])/g, ' $1').toLowerCase();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

/**
 * @param input string
 * i.e 'a.c123.2024-01\na.c123.5342.2023-01'
 * i.e '12323232\n12323232'
 * @returns string[]
 *  i.e ['a.c123.2024-01', 'a.c123.5342.2023-01']
 * i.e [12323232, 12323232]
 */
export const formatLineBreakOrCsvInput = (input: string): string[] => {
  const hasLineBreaks = /\n/.test(input);
  const hasCommas = /,/.test(input);

  if (hasLineBreaks && hasCommas) {
    return input
      .replace(/\n/g, '')
      .replace(/,/g, ',')
      .replace(/\s/g, '')
      .split(',');
  }

  if (hasLineBreaks) {
    return input.replace(/\n/g, ',').replace(/\s/g, '').split(',');
  }

  return input.replace(/\s/g, '').split(',');
};

/**
 *turns string array into numer array and filters out NaN
 * @param input string[]
 * i.e ['12323232', '12323232', ]
 * @returns number[]
 * (interrim would be ['12323232', '12323232', NaN ] before filtering)
 * i.e [12323232, 12323232]
 */
export const convertStringArrayToNumberArray = (input: string[]): number[] =>
  input.map(id => parseInt(id)).filter(id => !isNaN(id));

export const selectSorterFunction = (
  selectA: string,
  selectB: string,
): number => {
  const normalizedA = selectA.toLowerCase().replace('_', '');
  const normalizedB = selectB.toLowerCase().replace('_', '');
  if (normalizedA < normalizedB) {
    return -1;
  }
  if (normalizedA > normalizedB) {
    return 1;
  }
  return 0;
};

export interface CopayEligible {
  billId: number;
  eligible: boolean;
  reason?: string;
  billAmount?: number | null;
  costShareStatus?: string | null;
}

export const getMaxCopayAmount = (
  copayEligibleMap: Map<number, CopayEligible>,
): number => {
  let min = Infinity;
  for (const { eligible, billAmount } of copayEligibleMap.values()) {
    if (eligible && billAmount && billAmount > 0 && billAmount < min) {
      min = billAmount;
    }
  }
  return min === Infinity ? 0 : min;
};

export const getCopayHelperText = (
  copayAmount: number | null,
  maxCopayAmount: number,
): string => {
  if (copayAmount === null) {
    return '';
  }
  if (copayAmount <= 0) {
    return 'Must be greater than 0';
  }
  if (copayAmount > maxCopayAmount) {
    return `Must be less than or equal to ${formatCurrency(maxCopayAmount)}`;
  }
  return '';
};

export const checkCopayEligibleFromListData = (
  bill: BillTypeData,
): CopayEligible => {
  const hasSubmissions = bill.submissionId != null;
  const costShareStatus = bill.details?.costShare?.status ?? null;
  const isAwaitingRemit = costShareStatus === CostShareStatus.AWAITING_REMIT;

  if (!hasSubmissions) {
    return {
      billId: bill.billId,
      eligible: false,
      reason: 'Bill has no submissions',
      billAmount: bill.billAmount,
      costShareStatus,
    };
  }

  if (!isAwaitingRemit) {
    return {
      billId: bill.billId,
      eligible: false,
      reason: `Cost share status is "${costShareStatus ?? 'missing'}" (must be "awaiting_remit")`,
      billAmount: bill.billAmount,
      costShareStatus,
    };
  }

  return {
    billId: bill.billId,
    eligible: true,
    billAmount: bill.billAmount,
    costShareStatus,
  };
};
